// Configuration utility
// Uses environment variables for configuration

interface RuntimeConfig {
  API_URL: string
  KEYCLOAK_URL: string
  KEYCLOAK_REALM: string
  KEYCLOAK_CLIENT_ID: string
  USE_MOCKS: boolean
  GA4_MEASUREMENT_ID?: string
}

/**
 * Check if mock mode should be enabled based on URL parameter or session storage.
 * This allows accessibility testing tools to use mock data without authentication.
 *
 * How it works:
 * 1. Check if ?mock=1 is in URL → save to sessionStorage and enable mocks
 * 2. Check if mockMode is in sessionStorage → enable mocks
 * 3. Otherwise, use environment variable
 *
 * Mock mode persists across navigation within the same browser session.
 */
const checkMockMode = (): boolean => {
  // Check URL parameter first (works in all environments)
  const urlParams = new URLSearchParams(window.location.search)
  const mockParam = urlParams.get('mock')

  if (mockParam === '1') {
    // Save to session storage for persistence across navigation
    sessionStorage.setItem('mockMode', 'true')
    sessionStorage.setItem('showDemoBanner', 'true')
    return true
  } else if (mockParam === '0') {
    // Explicit disable via ?mock=0
    sessionStorage.removeItem('mockMode')
    sessionStorage.removeItem('showDemoBanner')
    return false
  }

  // Check session storage (persists across navigation)
  const mockFromStorage = sessionStorage.getItem('mockMode') === 'true'
  if (mockFromStorage) {
    return true
  }

  // In development, use mocks by default (unless explicitly disabled)
  if (import.meta.env.MODE === 'development') {
    return import.meta.env.VITE_USE_MOCKS !== 'false'
  }

  // Fall back to environment variable
  return import.meta.env.VITE_USE_MOCKS !== 'false'
}

// Get configuration from environment variables
export const getConfig = (): RuntimeConfig => {
  return {
    API_URL: import.meta.env.VITE_API_URL || '',
    KEYCLOAK_URL: import.meta.env.VITE_KEYCLOAK_URL || '',
    KEYCLOAK_REALM: import.meta.env.VITE_KEYCLOAK_REALM || 'eda',
    KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'smarteda-frontend',
    USE_MOCKS: checkMockMode(),
    GA4_MEASUREMENT_ID: import.meta.env.VITE_GA_MEASUREMENT_ID || undefined
  }
}

/**
 * Check if demo banner should be shown.
 * Shows banner when mock mode was explicitly activated via ?mock=1 parameter.
 * Does not show banner for default development mocks.
 */
export const isDemoMode = (): boolean => {
  return sessionStorage.getItem('showDemoBanner') === 'true'
}

/**
 * Clear mock mode from session storage
 * Useful for testing or when user wants to exit demo mode
 */
export const clearMockMode = (): void => {
  sessionStorage.removeItem('mockMode')
  sessionStorage.removeItem('showDemoBanner')
  window.location.reload()
}

// Log the current configuration (useful for debugging)
export const logConfig = () => {
  const config = getConfig()
  console.info('SmartEDA Configuration:', config)
}
